/**
 * @fileoverview Dropdown de atendentes com popover + busca server-side.
 *
 * Mesma UX do QueuePickerPopover (trigger arredondado, popover com search e lista
 * rolável), porém resolve os usuários via `GET /users?searchParam=...` a cada
 * digitação (debounced) e expõe apenas o usuário selecionado no `onChange`.
 * Cada atendente aparece com a foto (que já vem na mesma resposta) ou as iniciais.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  ButtonBase,
  Popover,
  Typography,
  IconButton,
  Divider,
  InputBase,
  CircularProgress,
  makeStyles,
} from "@material-ui/core";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import PersonAvatar from "../PersonAvatar";

import { Check, Search, X, ChevronDown } from "lucide-react";
const useStyles = makeStyles((theme) => ({
  trigger: {
    minWidth: 190,
    height: 48,
    borderRadius: 16,
    padding: theme.spacing(0, 1.5),
    justifyContent: "space-between",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255, 255, 255, 0.12)"
        : "1px solid rgba(15, 23, 42, 0.12)",
    background:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.03)"
        : "rgba(248, 250, 252, 0.9)",
  },
  triggerFullWidth: {
    width: "100%",
  },
  triggerContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.8),
    minWidth: 0,
    flex: 1,
  },
  triggerLabel: {
    display: "block",
    fontSize: "0.98rem",
    color: theme.palette.type === "dark" ? "#f8fafc" : "#111827",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  triggerIcon: {
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
  },
  placeholder: {
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.55)" : "#6b7280",
  },
  popover: {
    "& .MuiPopover-paper": {
      width: 320,
      maxWidth: "calc(100vw - 32px)",
      maxHeight: "calc(100vh - 48px)",
      display: "flex",
      flexDirection: "column",
      marginTop: theme.spacing(1),
      borderRadius: 20,
      overflow: "hidden",
      background: theme.palette.type === "dark" ? "#22262b" : "#ffffff",
      color: theme.palette.type === "dark" ? "#f8fafc" : "#111827",
      border:
        theme.palette.type === "dark"
          ? "1px solid rgba(255, 255, 255, 0.08)"
          : "1px solid rgba(15, 23, 42, 0.08)",
      boxShadow:
        theme.palette.type === "dark"
          ? "0 20px 48px rgba(0, 0, 0, 0.45)"
          : "0 20px 44px rgba(15, 23, 42, 0.16)",
    },
  },
  popoverHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.75, 2, 1),
  },
  popoverTitle: {
    fontSize: "1rem",
    fontWeight: 700,
  },
  popoverClose: {
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
  },
  popoverBody: {
    padding: theme.spacing(0, 1.5, 1.5),
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  searchField: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.7, 1.4),
    borderRadius: 16,
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255, 255, 255, 0.12)"
        : "1px solid rgba(15, 23, 42, 0.12)",
    background:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.03)"
        : "rgba(248, 250, 252, 0.9)",
  },
  searchIcon: {
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
  },
  searchInput: {
    flex: 1,
    color: theme.palette.type === "dark" ? "#f8fafc" : "#111827",
    "& input::placeholder": {
      color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.55)" : "#6b7280",
      opacity: 1,
    },
  },
  statusRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.25, 0.5, 1.25),
  },
  statusText: {
    fontSize: "0.95rem",
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.76)" : "#6b7280",
  },
  divider: {
    marginBottom: theme.spacing(0.75),
  },
  optionsScrollArea: {
    maxHeight: 320,
    minHeight: 0,
    overflowY: "auto",
    paddingRight: theme.spacing(0.5),
    marginRight: theme.spacing(-0.5),
  },
  option: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.1, 1.4),
    borderRadius: 14,
    marginBottom: theme.spacing(0.75),
    textAlign: "left",
    background: "transparent",
    "&:hover": {
      background:
        theme.palette.type === "dark"
          ? "rgba(255, 255, 255, 0.06)"
          : "rgba(15, 23, 42, 0.04)",
    },
  },
  optionSelected: {
    background:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.16)"
        : "rgba(59, 130, 246, 0.14)",
    "&:hover": {
      background:
        theme.palette.type === "dark"
          ? "rgba(255, 255, 255, 0.2)"
          : "rgba(59, 130, 246, 0.18)",
    },
  },
  optionMain: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    minWidth: 0,
  },
  optionLabel: {
    fontSize: "1rem",
    color: theme.palette.type === "dark" ? "#f8fafc" : "#111827",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  optionCheck: {
    marginLeft: theme.spacing(1.5),
    fontSize: "1rem",
    color: theme.palette.type === "dark" ? "#7aa2ff" : theme.palette.primary.main,
  },
  emptyState: {
    padding: theme.spacing(1.5, 0.5, 0.5),
    fontSize: "0.94rem",
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.6)" : "#6b7280",
    textAlign: "center",
  },
  loadingRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2, 0),
  },
}));

// A lista de abertura (busca vazia) fica guardada por alguns minutos: as fotos
// vêm em base64 na resposta, e sem isso cada abertura baixaria todas de novo.
const OPENING_LIST_TTL_MS = 5 * 60 * 1000;
let openingListCache = { key: null, at: 0, users: [] };

const openingListKey = () => localStorage.getItem("companyId") || "";

const cachedOpeningList = () =>
  openingListCache.key === openingListKey() &&
  Date.now() - openingListCache.at < OPENING_LIST_TTL_MS
    ? openingListCache.users
    : null;

const UserPickerPopover = ({
  value,
  onChange,
  placeholder,
  title,
  searchPlaceholder,
  clearLabel,
  noUsersFoundLabel,
  singleSelectedLabel,
  showingAllLabel,
  fullWidth = false,
  disabled = false,
  className,
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (!open) return undefined;
    const term = searchTerm.trim();
    let cancelled = false;
    const cached = term ? null : cachedOpeningList();
    if (cached) {
      setUsers(cached);
      setLoading(false);
      return undefined;
    }
    const run = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/users/", {
          params: { searchParam: term },
        });
        if (cancelled) return;
        const list = Array.isArray(data?.users) ? data.users : [];
        if (!term) openingListCache = { key: openingListKey(), at: Date.now(), users: list };
        setUsers(list);
      } catch (err) {
        if (!cancelled) toastError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    const delay = term ? 300 : 0;
    const handle = setTimeout(run, delay);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [open, searchTerm]);

  const handleOpen = (event) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchTerm("");
  };

  const handleSelect = (user) => {
    onChange(user);
    handleClose();
  };

  const triggerLabel = value?.name || placeholder;
  const isPlaceholder = !value;
  const selectedCount = value ? 1 : 0;

  const statusLabel = useMemo(() => {
    if (selectedCount === 1 && singleSelectedLabel) {
      return `1 ${singleSelectedLabel}`;
    }
    return showingAllLabel;
  }, [selectedCount, singleSelectedLabel, showingAllLabel]);

  return (
    <>
      <ButtonBase
        className={[
          classes.trigger,
          fullWidth ? classes.triggerFullWidth : "",
          className || "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={handleOpen}
        disabled={disabled}
      >
        <div className={classes.triggerContent}>
          {value && <PersonAvatar name={value.name} src={value.profileImage} size={24} />}
          <Typography
            className={`${classes.triggerLabel} ${
              isPlaceholder ? classes.placeholder : ""
            }`}
          >
            {triggerLabel}
          </Typography>
        </div>
        <ChevronDown className={classes.triggerIcon} />
      </ButtonBase>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        className={classes.popover}
      >
        <div className={classes.popoverHeader}>
          <Typography className={classes.popoverTitle}>{title}</Typography>
          <IconButton
            size="small"
            className={classes.popoverClose}
            onClick={handleClose}
          >
            <X size={18} />
          </IconButton>
        </div>

        <div className={classes.popoverBody}>
          <div className={classes.searchField}>
            <Search className={classes.searchIcon} />
            <InputBase
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={searchPlaceholder}
              className={classes.searchInput}
              autoFocus
            />
          </div>

          <div className={classes.statusRow}>
            <Typography className={classes.statusText}>{statusLabel}</Typography>
          </div>

          <Divider className={classes.divider} />

          <div className={classes.optionsScrollArea}>
            {clearLabel && value && (
              <ButtonBase
                className={classes.option}
                onClick={() => handleSelect(null)}
              >
                <Typography className={classes.optionLabel}>
                  {clearLabel}
                </Typography>
              </ButtonBase>
            )}

            {loading ? (
              <div className={classes.loadingRow}>
                <CircularProgress size={22} />
              </div>
            ) : users.length === 0 ? (
              <Typography className={classes.emptyState}>
                {noUsersFoundLabel}
              </Typography>
            ) : (
              users.map((u) => {
                const isSelected = value?.id === u.id;
                return (
                  <ButtonBase
                    key={u.id}
                    className={`${classes.option} ${
                      isSelected ? classes.optionSelected : ""
                    }`}
                    onClick={() => handleSelect(u)}
                  >
                    <div className={classes.optionMain}>
                      <PersonAvatar name={u.name} src={u.profileImage} size={28} />
                      <Typography className={classes.optionLabel}>
                        {u.name}
                      </Typography>
                    </div>
                    {isSelected && (
                      <Check className={classes.optionCheck} />
                    )}
                  </ButtonBase>
                );
              })
            )}
          </div>
        </div>
      </Popover>
    </>
  );
};

export default UserPickerPopover;
